/**
 * DE-136 — dedicated /devices/:id/telemetry subview.
 *
 * Page composition:
 *  - 8 latest-value metric tiles (always show the freshest sample;
 *    decoupled from chart hover by design — user-confirmed UX choice)
 *  - <TelemetryChartGroup /> — the interactive multi-series panel:
 *    range chips + ◀▶ slide + 📅 jump-to + brush-zoom + crosshair +
 *    tooltip + per-series legend toggle, all URL-persisted.
 *
 * Main Details remains focused on operational state (identity, status
 * cards, demo experience, settings, commands, recent events).
 * Operators who want to dig into CPU / memory / modem / projector
 * history come here intentionally.
 */
import { Link, useParams } from 'react-router-dom';
import { useDevice } from '../api/hooks';
import { TelemetryChartGroup } from '../components/telemetry/TelemetryChartGroup';

function Metric({
  label,
  value,
  unit,
}: {
  label: string;
  value: number | string | null | undefined;
  unit?: string;
}) {
  return (
    <div className="card">
      <div className="text-xs text-muted">{label}</div>
      <div className="text-2xl font-semibold tabular-nums">
        {value === null || value === undefined ? '—' : value}
        {unit && value !== null && value !== undefined && (
          <span className="text-sm text-muted ml-1 font-normal">{unit}</span>
        )}
      </div>
    </div>
  );
}

export function DeviceTelemetry() {
  const { id } = useParams();
  const device = useDevice(id);

  if (device.isLoading) return <p className="text-sm text-muted">Loading device…</p>;
  if (device.error) {
    return <p className="text-sm text-warn">{(device.error as Error).message}</p>;
  }
  if (!device.data) return null;

  const { device: d, latestTelemetry: t } = device.data;

  return (
    <div className="space-y-6">
      <div>
        <Link
          to={`/devices/${encodeURIComponent(d.thing)}`}
          className="text-xs text-muted hover:underline"
        >
          ← back to device
        </Link>
        <h1 className="text-2xl font-semibold tracking-tight font-mono mt-1">{d.thing}</h1>
        <p className="text-sm text-muted">Engineering telemetry — host + projector + environment history.</p>
      </div>

      <div className="grid grid-cols-4 gap-4">
        <Metric label="CPU" value={t?.cpu_percent ?? null} unit="%" />
        <Metric label="CPU temp" value={t?.cpu_temp_c ?? null} unit="°C" />
        <Metric label="Memory" value={t?.mem_used_mb ?? null} unit="MB" />
        <Metric label="/data" value={t?.disk_data_used_gb ?? null} unit="GB" />
        <Metric label="Modem" value={t?.modem_signal ?? null} />
        <Metric label="Load 1m" value={t?.load_1min ?? null} />
        <Metric
          label="Uptime"
          value={t?.uptime_s ? Math.round(t.uptime_s / 3600) : null}
          unit="h"
        />
        <Metric
          label="Throttled"
          value={
            t?.throttled_now === true ? 'yes' : t?.throttled_now === false ? 'no' : null
          }
        />
      </div>

      <TelemetryChartGroup thing={d.thing} />
    </div>
  );
}
